import { Suspense } from "react";
import { Switch, Redirect, Route } from "react-router-dom";
import { QueryErrorResetBoundary } from "react-query";
import { ErrorBoundary } from "react-error-boundary";
import { LoginRoute, PrivateRoute } from "marvin-auth-kit";
import { ConfirmProvider } from "material-ui-confirm";
import { useTranslation } from "react-i18next";
import "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";

import { RootLayout } from "./layouts/RootLayout";
import { PageLoader } from "./components/page/loading/PageLoader";
import { LoginPage } from "./pages/LoginPage";
import { PasswordForgotPage } from "./pages/PasswordForgotPage";
import { CasesPage } from "./pages/CasesPage";
import { PasswordResetConfirmPage } from "./pages/PasswordResetConfirmPage";
import { CasePage } from "./pages/CasePage";
import { BackgroundConcentrationsPage } from "./pages/BackgroundConcentrationsPage";
import { MeteoPage } from "./pages/MeteoPage";
import { CaseCreatePage } from "./pages/CaseCreatePage";
import { CaseResultsPage } from "./pages/CaseResultsPage";
import { RootErrorFallback } from "./components/error-boundaries/RootErrorBoundary";
import { UsersPage } from "./pages/UsersPage";
import { UserPage } from "./pages/UserPage";
import { LoginRedirectPage } from "./pages/LoginRedirectPage";
import { AppLayout } from "./layouts/AppLayout";
import { LoginLinkPage } from "./pages/auth/LoginLinkPage";
import { SegmentEditTestPage } from "./pages/SegmentEditTestPage";

interface Props {}

export function App(props: Props) {
  const { t } = useTranslation();

  return render();

  function render() {
    return (
      <>
        <ConfirmProvider
          defaultOptions={{
            confirmationText: t("label.ok"),
            cancellationText: t("label.cancel"),
          }}
        >
          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary FallbackComponent={RootErrorFallback} onReset={reset}>
                <RootLayout>
                  <Suspense fallback={<PageLoader />}>
                    <Switch>
                      <LoginRoute
                        path={["/login"]}
                        redirectTo="/login-redirect"
                        component={LoginPage}
                        loader={<PageLoader />}
                      />
                      <PrivateRoute path="/login-redirect" component={LoginRedirectPage} loader={<PageLoader />} />

                      <Route path="/password/forgot">
                        <PasswordForgotPage />
                      </Route>
                      <Route path={["/password/reset/:token", "/password/reset"]}>
                        <PasswordResetConfirmPage />
                      </Route>
                      <Route path={["/loginlink"]}>
                        <LoginLinkPage />
                      </Route>

                      <PrivateRoute
                        exact
                        path={["/cases", "/custom-meteo", "/background-concentrations", "/users", "/users/:userId"]}
                        component={AppLayout}
                      >
                        <AppLayout>
                          <PrivateRoute exact path="/cases" component={CasesPage} loader={<PageLoader />} />
                          <PrivateRoute exact path="/custom-meteo" component={MeteoPage} loader={<PageLoader />} />
                          <PrivateRoute
                            exact
                            path="/background-concentrations"
                            component={BackgroundConcentrationsPage}
                            loader={<PageLoader />}
                          />
                          <PrivateRoute exact path="/users" component={UsersPage} loader={<PageLoader />} />
                          <PrivateRoute exact path="/users/:userId" component={UserPage} loader={<PageLoader />} />
                        </AppLayout>
                      </PrivateRoute>

                      <PrivateRoute path="/cases/new" component={CaseCreatePage} loader={<PageLoader />} />
                      <PrivateRoute
                        path={"/cases/:caseId/results"}
                        component={CaseResultsPage}
                        loader={<PageLoader />}
                      />
                      <PrivateRoute
                        path={["/cases/:caseId/scenarios/:scenarioId", "/cases/:caseId"]}
                        component={CasePage}
                        loader={<PageLoader />}
                      />
                      <Redirect to="/login" />
                    </Switch>
                  </Suspense>
                </RootLayout>
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        </ConfirmProvider>
      </>
    );
  }
}
